import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BaseDetailData, PriceConfigService, SelectedCustomerService } from 'gung-standard';
import { JeevesProductDetailsComponent } from 'gung-standard-jeeves';
import { GungTabChangeEvent } from 'gung-common';
import { Router } from '@angular/router';
import { first, forkJoin, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'car-liner-product-details',
  templateUrl: './car-liner-product-details.component.html',
  styleUrls: ['./car-liner-product-details.component.scss'],
  standalone: false
})
export class CarLinerProductDetailsComponent extends JeevesProductDetailsComponent implements OnInit, OnDestroy {
  private priceConfigService = inject(PriceConfigService);
  private selectedCustomerService = inject(SelectedCustomerService);
  private router = inject(Router);

  public includedItemIds: string[] = [];
  public sparePartsIds: string[] = [];
  public powerUnits: string[] = [];
  public hidePrice: boolean;
  public isCaroliner: boolean = environment.projectName === 'caroliner';
  public isJosam: boolean = environment.projectName === 'josam';
  public isBlackhawkProject: boolean = environment.projectName === 'blackhawk';
  public activeTab: string;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    super.ngOnInit();
    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
    this.activeTab = this.route.snapshot.queryParams.tab;
  }

  tabChanged(event: GungTabChangeEvent): void {
    this.activeTab = event.nextId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: event.nextId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected handleObservable(observable: Observable<BaseDetailData>): void {
    observable.subscribe(d => {
      this.pimRelatedProductsIds = [];
      this.addonIds = [];
      this.includedItemIds = [];
      const { product, availability, price, user } = d;
      if (product.extra.artv) {
        this.addonIds = product.extra.artv.filter(r => r.q_ej_ecom !== '1').map(r => r.artnrtillvalart);
      }
      if (product.extra.pim && product.extra.pim.relatedProducts) {
        this.pimRelatedProductsIds = product.extra.pim.relatedProducts.split(',');
      }

      if (product?.extra.pim?.powerUnits) {
        this.powerUnits = product.extra.pim.powerUnits.split(',');
      }

      if (product.extra.sr) {
        product.extra.sr.forEach(includedItem => {
          if (includedItem.bartnrstrukt) {
            this.includedItemIds.push(includedItem.bartnrstrukt);
          }
        });
      }

      if (product.extra.ar.q_reservdelsnr) {
        this.productService
          .getProduct(product.extra.ar.q_reservdelsnr)
          .pipe(first())
          .subscribe(product => {
            if (product.extra.sr.length > 0) {
              for (const sparePart of product.extra.sr) {
                this.sparePartsIds.push(sparePart.bartnrstrukt);
              }
            }
          });
      }

      // filter out blocked products
      const allIds = new Set([
        ...this.addonIds,
        ...this.pimRelatedProductsIds,
        ...this.powerUnits,
        ...this.includedItemIds,
        ...this.sparePartsIds
      ]);

      this.selectedCustomerService
        .getSelectedCustomer()
        .pipe(
          first(),
          switchMap(selectedCustomer =>
            forkJoin({
              customer: of([selectedCustomer]),
              products: this.productService
                .getProductsByAssortment(
                  this.isBlackhawkProject
                    ? 'BLACKHAWK_' + selectedCustomer.extra.kus.prislista
                    : selectedCustomer.extra.kus.prislista
                )
                .pipe(first()),
              blockedProducts: this.productService.getBlockedProducts(Array.from(allIds)).pipe(first())
            })
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(({ customer, products, blockedProducts }) => {
          const assortmentIds = new Set(products.map(p => p.id));

          this.addonIds = this.addonIds.filter(id => assortmentIds.has(id)).filter(id => !blockedProducts[id]);

          this.pimRelatedProductsIds = this.pimRelatedProductsIds
            .filter(id => assortmentIds.has(id))
            .filter(id => !blockedProducts[id]);

          this.powerUnits = this.powerUnits.filter(id => assortmentIds.has(id)).filter(id => !blockedProducts[id]);

          this.includedItemIds = this.includedItemIds
            .filter(id => assortmentIds.has(id))
            .filter(id => !blockedProducts[id]);

          this.sparePartsIds = this.sparePartsIds
            .filter(id => assortmentIds.has(id))
            .filter(id => !blockedProducts[id]);
        });



      this.product = product;
      this.availability = availability;
      this.price = price;

      this.isAnonymous = user.roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;

      this.initImagesCarousel();
    });
  }
}
